import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  Language,
  translations,
  getDirection,
} from './thi-translations';
import { normalizeThiLang } from './thi-lang.util';

interface UseCaseTab {
  icon: string;
  title: string;
  problem: string;
  solution: string;
  benefit: string;
}

@Component({
  selector: 'app-thi-landing-main',
  templateUrl: './thi-landing-main.component.html',
  styleUrls: ['./thi-landing-main.component.scss'],
})
export class ThiLandingMainComponent implements OnInit, OnChanges {
  @Input() lang: Language = 'ar';

  t = translations.ar;
  dir: 'rtl' | 'ltr' = 'rtl';
  activeUseCase = 0;
  openFaqIndex = -1;

  benefits: { icon: string; title: string; desc: string }[] = [];
  features: { icon: string; title: string; desc: string }[] = [];
  aiSteps: { icon: string; title: string; desc: string }[] = [];
  faqs: { q: string; a: string }[] = [];
  testimonials: { quote: string; author: string; role: string }[] = [];
  useCases: UseCaseTab[] = [];

  ngOnInit(): void {
    this.applyLang();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lang']) {
      this.applyLang();
    }
  }

  get arrowIcon(): string {
    return this.dir === 'rtl' ? 'fa fa-arrow-left' : 'fa fa-arrow-right';
  }

  get moreFaqLabel(): string {
    if (this.lang === 'ar') return 'المزيد من الأسئلة';
    if (this.lang === 'he') return 'עוד שאלות';
    return 'More Questions';
  }

  get aiBadgeLabel(): string {
    if (this.lang === 'ar') return 'مدعوم بالذكاء الاصطناعي';
    if (this.lang === 'he') return 'מופעל ב-AI';
    return 'AI-Powered';
  }

  toggleFaq(i: number): void {
    this.openFaqIndex = this.openFaqIndex === i ? -1 : i;
  }

  private applyLang(): void {
    this.lang = normalizeThiLang(this.lang as unknown as string);
    this.t = translations[this.lang];
    this.dir = getDirection(this.lang);
    const t = this.t;

    this.benefits = [
      { icon: 'fa fa-clock-o', title: t.benefits.time, desc: t.benefits.timeDesc },
      { icon: 'fa fa-file-text-o', title: t.benefits.organize, desc: t.benefits.organizeDesc },
      { icon: 'fa fa-bar-chart', title: t.benefits.insights, desc: t.benefits.insightsDesc },
    ];

    this.features = [
      { icon: 'fa fa-file-text-o', title: t.features.invoicing, desc: t.features.invoicingDesc },
      { icon: 'fa fa-file-o', title: t.features.expenses, desc: t.features.expensesDesc },
      { icon: 'fa fa-users', title: t.features.clients, desc: t.features.clientsDesc },
      { icon: 'fa fa-bar-chart', title: t.features.reports, desc: t.features.reportsDesc },
      { icon: 'fa fa-money', title: t.features.multiCurrency, desc: t.features.multiCurrencyDesc },
      { icon: 'fa fa-download', title: t.features.export, desc: t.features.exportDesc },
    ];

    this.aiSteps = [
      { icon: 'fa fa-camera', title: t.ai.step1, desc: t.ai.step1Desc },
      { icon: 'fa fa-microchip', title: t.ai.step2, desc: t.ai.step2Desc },
      { icon: 'fa fa-tag', title: t.ai.step3, desc: t.ai.step3Desc },
      { icon: 'fa fa-line-chart', title: t.ai.step4, desc: t.ai.step4Desc },
    ];

    this.faqs = [
      { q: t.faq.q1, a: t.faq.a1 },
      { q: t.faq.q2, a: t.faq.a2 },
      { q: t.faq.q3, a: t.faq.a3 },
      { q: t.faq.q4, a: t.faq.a4 },
      { q: t.faq.q5, a: t.faq.a5 },
      { q: t.faq.q6, a: t.faq.a6 },
    ];

    this.testimonials = [
      { quote: t.testimonials.t1, author: t.testimonials.t1Author, role: t.testimonials.t1Role },
      { quote: t.testimonials.t2, author: t.testimonials.t2Author, role: t.testimonials.t2Role },
      { quote: t.testimonials.t3, author: t.testimonials.t3Author, role: t.testimonials.t3Role },
    ];

    const L = this.lang;
    this.useCases = [
      {
        icon: 'fa fa-briefcase',
        title: t.useCases.smallBusiness,
        problem:
          L === 'ar'
            ? 'وقت طويل يُهدر في إعداد الفواتير يدويًا'
            : L === 'he'
              ? 'זמן רב מתבזבז על הכנת חשבוניות ידנית'
              : 'Too much time spent on manual invoicing',
        solution:
          L === 'ar'
            ? 'إنشاء فواتير احترافية بنقرات قليلة'
            : L === 'he'
              ? 'יצירת חשבוניות מקצועיות בכמה קליקים'
              : 'Create professional invoices in a few clicks',
        benefit:
          L === 'ar'
            ? 'وفر ساعات أسبوعيًا وركز على تنمية مشروعك'
            : L === 'he'
              ? 'חסוך שעות בשבוע והתמקד בצמיחת העסק'
              : 'Save hours weekly and focus on growing your business',
      },
      {
        icon: 'fa fa-shopping-bag',
        title: t.useCases.retail,
        problem:
          L === 'ar'
            ? 'صعوبة تتبع المصروفات اليومية المتعددة'
            : L === 'he'
              ? 'קושי לעקוב אחר הוצאות יומיות מרובות'
              : 'Difficulty tracking multiple daily expenses',
        solution:
          L === 'ar'
            ? 'تصوير الإيصالات وتسجيل تلقائي بالذكاء الاصطناعي'
            : L === 'he'
              ? 'צילום קבלות ורישום אוטומטי עם AI'
              : 'Snap receipts and auto-log with AI',
        benefit:
          L === 'ar'
            ? 'رؤية واضحة لمصروفاتك اليومية والشهرية'
            : L === 'he'
              ? 'תמונה ברורה של ההוצאות היומיות והחודשיות'
              : 'Clear view of daily and monthly expenses',
      },
      {
        icon: 'fa fa-building',
        title: t.useCases.medium,
        problem:
          L === 'ar'
            ? 'إدارة فواتير ومصروفات فريق متعدد'
            : L === 'he'
              ? 'ניהול חשבוניות והוצאות של צוות מרובה'
              : 'Managing invoices and expenses for multiple team members',
        solution:
          L === 'ar'
            ? 'صلاحيات متدرجة وتقارير موحدة'
            : L === 'he'
              ? 'הרשאות מדורגות ודוחות מאוחדים'
              : 'Tiered permissions and unified reports',
        benefit:
          L === 'ar'
            ? 'تحكم كامل مع تقارير شاملة للإدارة'
            : L === 'he'
              ? 'שליטה מלאה עם דוחות מקיפים להנהלה'
              : 'Full control with comprehensive management reports',
      },
      {
        icon: 'fa fa-calculator',
        title: t.useCases.accountants,
        problem:
          L === 'ar'
            ? 'إدارة حسابات عملاء متعددين بكفاءة'
            : L === 'he'
              ? 'ניהול חשבונות של לקוחות מרובים ביעילות'
              : 'Efficiently managing multiple client accounts',
        solution:
          L === 'ar'
            ? 'لوحة تحكم موحدة لكل الزبائن'
            : L === 'he'
              ? 'לוח בקרה מאוחד לכל הלקוחות'
              : 'Unified dashboard for all clients',
        benefit:
          L === 'ar'
            ? 'تقارير جاهزة وتصدير سهل للبيانات'
            : L === 'he'
              ? 'דוחות מוכנים וייצוא נתונים קל'
              : 'Ready reports and easy data export',
      },
    ];

    if (this.activeUseCase >= this.useCases.length) {
      this.activeUseCase = 0;
    }
  }
}
